import { useEffect, useState } from 'react'
import { CheckCircle2, Eye, FileWarning, X } from 'lucide-react'
import { api } from '../lib/api'
import type { ApprovalEvidenceStatus } from '../types'

const documentLabels: Record<string, string> = {
  CONTRACT: '合同原件',
  ORDER: '订单原件',
  PURCHASE_ORDER: '采购订单原件',
  OTHER: '其他证据',
}

export function ControlledEvidenceReview({ status, onChange }: {
  status?: ApprovalEvidenceStatus
  onChange: (status: ApprovalEvidenceStatus) => void
}) {
  const [previewing, setPreviewing] = useState<ApprovalEvidenceStatus['files'][number]>()
  const [previewUrl, setPreviewUrl] = useState<string>()
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState('')
  const attachmentId = previewing?.attachmentId
  const authorizationId = status?.authorizationId
  const licenseId = status?.licenseId

  // The preview endpoint is authenticated and deliberately sends X-Frame-Options: DENY.
  // Fetching it first keeps the security headers intact while allowing the browser to render
  // the returned bytes from a short-lived, same-origin object URL.
  useEffect(() => {
    let active = true
    let objectUrl: string | undefined
    setPreviewUrl(undefined)
    setPreviewError('')
    if (!attachmentId || !authorizationId || !licenseId) {
      setPreviewLoading(false)
      return () => undefined
    }
    setPreviewLoading(true)
    void api.previewCommercialAuthorizationAttachment(authorizationId, attachmentId, licenseId)
      .then(blob => {
        if (!active) return
        objectUrl = URL.createObjectURL(blob)
        setPreviewUrl(objectUrl)
        // Refresh the receipt state after the server has recorded this controlled read.
        void api.approvalEvidence(licenseId).then(onChange)
      })
      .catch(value => {
        if (active) setPreviewError(value instanceof Error ? value.message : '合同原件预览加载失败')
      })
      .finally(() => { if (active) setPreviewLoading(false) })
    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [attachmentId, authorizationId, licenseId, onChange])

  async function refresh() {
    if (!status) return
    const next = await api.approvalEvidence(status.licenseId)
    onChange(next)
  }

  if (!status) return <div className="controlled-evidence-status">正在读取合同原件查看状态...</div>
  if (status.legacyEvidenceException) return <div className="controlled-evidence-status is-warning">
    <FileWarning size={17} /><span><b>历史证据例外</b>该商业授权来自历史数据，没有平台留存原件；审批结论会明确记录此例外。</span>
  </div>

  return <>
    <div className="controlled-evidence-list">
      {status.files.map(file => <article key={file.attachmentId} className={file.viewedAt ? 'is-viewed' : ''}>
        <div><b>{documentLabels[file.documentType] || file.documentType} · {file.fileName}</b><code>{file.sha256}</code>
          <small>{file.viewedAt ? `本人已于 ${new Date(file.viewedAt).toLocaleString('zh-CN', { hour12: false })} 查看` : '本人尚未查看，不能审批通过'}</small></div>
        <button type="button" className="button button--ghost button--compact" onClick={() => setPreviewing(file)}>
          {file.viewedAt ? <CheckCircle2 size={15} /> : <Eye size={15} />}{file.viewedAt ? '再次查看' : '查看原件'}
        </button>
      </article>)}
    </div>
    <div className={`controlled-evidence-summary ${status.complete ? 'is-complete' : ''}`}>
      {status.complete ? <CheckCircle2 size={16} /> : <FileWarning size={16} />}
      {status.complete ? '必需合同原件已全部查看，可以提交审批结论。' : `还有 ${status.files.filter(file => !file.viewedAt).length} 份必需原件未查看。`}
    </div>
    {previewing && <div className="modal-layer evidence-preview-layer">
      <section className="evidence-preview-modal" role="dialog" aria-modal="true" aria-label={`查看原件 ${previewing.fileName}`}>
        <header><div><small>{documentLabels[previewing.documentType] || previewing.documentType}</small><h3>{previewing.fileName}</h3></div><button type="button" className="icon-button" aria-label="关闭原件预览" onClick={() => { setPreviewing(undefined); void refresh() }}><X size={19} /></button></header>
        <div className="evidence-preview-content">
          {previewLoading && <div className="controlled-evidence-status">正在加载合同原件...</div>}
          {previewError && <div className="controlled-evidence-status is-warning"><FileWarning size={17} /><span>{previewError}</span></div>}
          {previewUrl && <iframe title={previewing.fileName} src={previewUrl} />}
        </div>
        <footer><span>本次受控访问会记录查看人、时间、文件摘要和来源地址。</span><button type="button" className="button button--primary" onClick={() => { setPreviewing(undefined); void refresh() }}>完成查看</button></footer>
      </section>
    </div>}
  </>
}
